import './SotGa4PendingSegmentationChanges.scss';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { MdClose } from 'react-icons/md';
import { List, RowComponentProps } from 'react-window';
import { Appearance } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import {
  SotGa4EditsMap,
  SotGa4EditsMapAction,
  SotGa4EditsMapActionType,
  SotGa4Row
} from './sot-ga4-mapping-tool-constants';

interface SotGa4PendingSegmentationChangesProps {
  incompleteUnlabeledEditsMap: SotGa4EditsMap;
  completeUnlabeledEditsMap: SotGa4EditsMap;
  labeledEditsMap: SotGa4EditsMap;
  dispatchUnlabeledEditsMap: Dispatch<SotGa4EditsMapAction>;
  dispatchLabeledEditsMap: Dispatch<SotGa4EditsMapAction>;
  setShowPendingChanges: Dispatch<SetStateAction<boolean>>;
}

const SectionKind = {
  INCOMPLETE_UNLABELED: 'incompleteUnlabeled',
  COMPLETE_UNLABELED: 'completeUnlabeled',
  LABELED: 'labeled'
} as const;
type SectionKindType = (typeof SectionKind)[keyof typeof SectionKind];

type FlatItem =
  | { kind: 'header'; title: string }
  | { kind: 'row'; section: SectionKindType; campaignKey: string; row: SotGa4Row };

const SECTION_TITLES: Record<SectionKindType, string> = {
  [SectionKind.INCOMPLETE_UNLABELED]: 'Incomplete segment assignments',
  [SectionKind.COMPLETE_UNLABELED]: 'Complete segment assignments',
  [SectionKind.LABELED]: 'Segment updates'
};

const HEADER_HEIGHT = 50;
const ROW_BASE_HEIGHT = 96;
const ROW_PER_SEGMENT_HEIGHT = 40;
const ROW_BOTTOM_GAP = 16;

const getItemHeight = (item: FlatItem): number => {
  if (item.kind === 'header') {
    return HEADER_HEIGHT;
  }
  const segmentCount = item.row.edits ? Object.keys(item.row.edits).length : 0;
  return ROW_BASE_HEIGHT + segmentCount * ROW_PER_SEGMENT_HEIGHT + ROW_BOTTOM_GAP;
};

interface RowData {
  items: FlatItem[];
  onRemoveSegment: (section: SectionKindType, campaignKey: string, segmentName: string) => void;
}

const VirtualRow = ({ index, style, items, onRemoveSegment }: RowComponentProps<RowData>) => {
  const item = items[index];
  if (!item) {
    return null;
  }

  if (item.kind === 'header') {
    return (
      <div style={style} className='virtual-header-row'>
        <span className='edits-title'>{item.title}</span>
      </div>
    );
  }

  const { section, campaignKey, row } = item;

  return (
    <div style={style} className='virtual-item-row'>
      <div className='pending-edit-item'>
        <div className='campaign-info'>
          <strong>Campaign:</strong> {row.campaign_name || row.campaign_id || 'Unknown'}
          {row.ad_group_name && <span> | Ad Group: {row.ad_group_name}</span>}
          {row.ad_name && <span> | Ad: {row.ad_name}</span>}
        </div>
        <div className='segment-edits'>
          {row.edits &&
            Object.entries(row.edits).map(([segmentName, edit]) => {
              if (!edit) {
                return null;
              }
              return (
                <div
                  key={segmentName}
                  className='segment-edit'
                  data-testid={`sot-ga4-pending-edit-${campaignKey}-${segmentName}`}
                >
                  <span className='segment-name'>{segmentName}:</span>
                  <span className='segment-value'>{edit.label}</span>
                  <IconButton
                    appearance={Appearance.EMPTY}
                    className='remove-edit'
                    onClick={() => onRemoveSegment(section, campaignKey, segmentName)}
                  >
                    <MdClose />
                  </IconButton>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export const SotGa4PendingSegmentationChanges = ({
  incompleteUnlabeledEditsMap,
  completeUnlabeledEditsMap,
  labeledEditsMap,
  dispatchUnlabeledEditsMap,
  dispatchLabeledEditsMap,
  setShowPendingChanges
}: SotGa4PendingSegmentationChangesProps) => {
  const items = useMemo<FlatItem[]>(() => {
    const flat: FlatItem[] = [];
    const sections: [SectionKindType, SotGa4EditsMap][] = [
      [SectionKind.INCOMPLETE_UNLABELED, incompleteUnlabeledEditsMap],
      [SectionKind.COMPLETE_UNLABELED, completeUnlabeledEditsMap],
      [SectionKind.LABELED, labeledEditsMap]
    ];

    for (const [section, map] of sections) {
      if (Object.keys(map).length === 0) {
        continue;
      }
      flat.push({ kind: 'header', title: SECTION_TITLES[section] });
      for (const campaignKey of Object.keys(map)) {
        flat.push({ kind: 'row', section, campaignKey, row: map[campaignKey] });
      }
    }

    return flat;
  }, [incompleteUnlabeledEditsMap, completeUnlabeledEditsMap, labeledEditsMap]);

  const handleRemoveSegment = useCallback(
    (section: SectionKindType, campaignKey: string, segmentName: string) => {
      const dispatch =
        section === SectionKind.LABELED ? dispatchLabeledEditsMap : dispatchUnlabeledEditsMap;
      dispatch({
        type: SotGa4EditsMapActionType.DELETE_SEGMENT_EDIT,
        campaign_keys: [campaignKey],
        segmentName
      });
    },
    [dispatchLabeledEditsMap, dispatchUnlabeledEditsMap]
  );

  const rowProps = useMemo<RowData>(
    () => ({ items, onRemoveSegment: handleRemoveSegment }),
    [items, handleRemoveSegment]
  );

  const rowHeight = useCallback((index: number) => getItemHeight(items[index]), [items]);

  const handleClose = useCallback(() => setShowPendingChanges(false), [setShowPendingChanges]);

  return (
    <div className='pending-segmentation-changes-container' data-testid='sot-ga4-pending-changes'>
      <div className='pending-segmentation-changes-header'>
        Pending Changes
        <IconButton
          appearance={Appearance.EMPTY}
          className='close-button'
          data-testid='sot-ga4-pending-close'
          onClick={handleClose}
        >
          <MdClose />
        </IconButton>
      </div>
      <div className='pending-segmentation-changes-body'>
        {items.length > 0 && (
          <List
            className='pending-changes-list'
            rowComponent={VirtualRow}
            rowCount={items.length}
            rowHeight={rowHeight}
            rowProps={rowProps}
            overscanCount={6}
          />
        )}
      </div>
    </div>
  );
};
